import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const TeacherAssignments = () => {
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teachersResponse, studentsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/teachers`),
        axios.get(`${API_BASE_URL}/api/admin/students`)
      ]);

      setTeachers(teachersResponse.data);
      setStudents(studentsResponse.data);
    } catch (error) {
      setError('Failed to load data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAssignments = (teacher) => {
    setEditingTeacher(teacher);
    setSelectedStudents(teacher.assignedStudents?.map(s => s._id) || []);
    setError('');
    setSuccess('');
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSaveAssignments = async () => {
    try {
      setLoading(true);
      await axios.put(`${API_BASE_URL}/api/admin/teachers/${editingTeacher._id}/assign-students`, {
        studentIds: selectedStudents
      });

      setSuccess(`Updated student assignments for ${editingTeacher.name}`);
      await fetchData();
      setEditingTeacher(null);
      setSelectedStudents([]);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTeacher(null);
    setSelectedStudents([]);
    setError('');
  };

  if (loading && !editingTeacher) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          Loading teacher assignments...
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <Link to="/admin" className="btn btn-secondary">
          ← Back to Admin Dashboard
        </Link>
        <h1 style={{ margin: 0 }}>👩‍🏫 Teacher Student Assignments</h1>
      </div>

      <div style={{ 
        padding: '16px', 
        backgroundColor: '#e7f3ff', 
        borderRadius: '8px', 
        border: '1px solid #b3d9ff',
        marginBottom: '24px'
      }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#0066cc' }}>ℹ️ Teacher Assignment System</h4>
        <p style={{ margin: '0', color: '#0066cc', fontSize: '14px' }}>
          Assign specific students and graders to teachers. If no assignments are made, teachers can view all students and graders. 
          Once assignments are made, teachers will only see their assigned users.
        </p>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {success}
        </div>
      )}

      {editingTeacher ? (
        <div className="card">
          <h2>Assign Students & Graders to {editingTeacher.name}</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Select which students and graders this teacher can view and monitor. Leave empty to allow viewing all users.
          </p>

          <div style={{ marginBottom: '20px' }}>
            <h4>Available Students & Graders ({students.length})</h4>
            <div style={{ 
              maxHeight: '400px', 
              overflowY: 'auto', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              padding: '12px'
            }}>
              {students.length === 0 ? (
                <p style={{ color: '#666', textAlign: 'center', margin: '20px 0' }}>
                  No students available
                </p>
              ) : (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {students.map((student) => (
                    <label 
                      key={student._id} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: selectedStudents.includes(student._id) ? '#e8f5e8' : 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student._id)}
                        onChange={() => handleStudentToggle(student._id)}
                        style={{ margin: 0 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong>{student.name}</strong>
                          <span 
                            className={`badge ${student.role === 'grader' ? 'badge-warning' : 'badge-success'}`}
                            style={{ fontSize: '10px', padding: '2px 6px' }}
                          >
                            {student.role === 'grader' ? 'Grader' : 'Student'}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          ID: {student.studentId} • {student.email}
                          {student.stats && (
                            <span> • {student.stats.totalSubmissions} submissions</span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <strong>Selected Students: {selectedStudents.length}</strong>
            {selectedStudents.length === 0 && (
              <span style={{ color: '#666', marginLeft: '8px' }}>
                (Teacher will see all students)
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleSaveAssignments}
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Assignments'}
            </button>
            <button
              onClick={handleCancelEdit}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <h2>Teachers and Their Student Assignments</h2>
          
          {teachers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <h3>No Teachers Found</h3>
              <p>Create teacher accounts first before managing assignments.</p>
              <Link to="/admin/create-account" className="btn btn-primary">
                Create Teacher Account
              </Link>
            </div>
          ) : (
            <div className="grid grid-1">
              {teachers.map((teacher) => (
                <div key={teacher._id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 8px 0' }}>{teacher.name}</h3>
                      <div style={{ color: '#666', marginBottom: '12px' }}>
                        <div>ID: {teacher.studentId}</div>
                        <div>{teacher.email}</div>
                        <div>Created: {new Date(teacher.createdAt).toLocaleDateString()}</div>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <h4 style={{ margin: '0 0 8px 0' }}>Assigned Students:</h4>
                        {teacher.assignedStudents && teacher.assignedStudents.length > 0 ? (
                          <div>
                            <div style={{ marginBottom: '8px' }}>
                              <strong>{teacher.assignedStudents.length} students assigned</strong>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {teacher.assignedStudents.map((student) => (
                                <span 
                                  key={student._id}
                                  className="badge badge-primary"
                                  style={{ fontSize: '12px' }}
                                >
                                  {student.name} (ID: {student.studentId})
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div style={{ 
                            padding: '8px 12px', 
                            backgroundColor: '#fff3cd', 
                            borderRadius: '4px',
                            border: '1px solid #ffeaa7'
                          }}>
                            <strong>Can view all students</strong> (no specific assignments)
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleEditAssignments(teacher)}
                      className="btn btn-primary"
                      style={{ marginLeft: '16px' }}
                    >
                      Edit Assignments
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card">
        <h3>Assignment Guidelines</h3>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
          <li><strong>No assignments:</strong> Teacher can view all students in the system</li>
          <li><strong>With assignments:</strong> Teacher can only view assigned students</li>
          <li><strong>Dynamic filtering:</strong> Teacher dashboard automatically filters based on assignments</li>
          <li><strong>Submission tracking:</strong> Teachers see only submissions from their assigned students</li>
          <li><strong>Updates apply immediately:</strong> Changes take effect as soon as they're saved</li>
        </ul>
      </div>
    </div>
  );
};

export default TeacherAssignments;
